<template>
  <div>
    <v-row align="center" class="mb-1">
      <v-col cols="4">
        <v-text-field
          v-model="ricerca"
          prepend-inner-icon="mdi-magnify"
          label="Cerca prodotto, varietà o colore…"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="3">
        <v-switch
          v-model="solo_scoperti"
          inset
          dense
          hide-details
          class="mt-0"
          label="Solo scoperti"
        />
      </v-col>
      <v-col cols="3">
        <v-switch
          v-model="mostra_escluse"
          inset
          dense
          hide-details
          class="mt-0"
          label="Mostra escluse"
        />
      </v-col>
      <v-col cols="2" class="text-right">
        <span class="text-caption grey--text mr-3" title="Tasto destro su una riga varietà">
          <v-icon x-small class="mr-1">mdi-cursor-default-click-outline</v-icon>
          tasto destro
        </span>
        <v-btn small text @click="alterna_tutto">
          <v-icon left small>
            {{ tutto_chiuso ? "mdi-expand-all" : "mdi-collapse-all" }}
          </v-icon>
          {{ tutto_chiuso ? "Espandi" : "Comprimi" }}
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="errore" type="error" text dense>
      Non è stato possibile caricare i colori.
      <v-btn small text color="error" @click="carica">Riprova</v-btn>
    </v-alert>

    <div v-if="caricamento" class="py-12 d-flex justify-center">
      <v-progress-circular indeterminate color="green" />
    </div>

    <template v-else>
      <!-- Gli scoperti in testa: è la ragione per cui si apre questa pagina -->
      <v-card v-if="scoperti.length" outlined class="mb-5 scoperti">
        <v-card-title
          class="text-subtitle-1 red--text pb-1"
          style="cursor: pointer"
          @click="scoperti_aperti = !scoperti_aperti"
        >
          <v-icon color="red" class="mr-2">mdi-alert</v-icon>
          {{ scoperti.length }} varietà ordinate oltre la produzione
          <v-spacer />
          <v-icon color="red">
            {{ scoperti_aperti ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
        </v-card-title>
        <v-expand-transition>
        <v-card-text v-if="scoperti_aperti" class="pb-2">
          <div
            v-for="gruppo in scoperti_raggruppati"
            :key="gruppo.id_prodotto"
            class="mb-3"
          >
            <div class="testata_scoperto">
              <span>Ø{{ gruppo.diametro }} · {{ gruppo.nome_prodotto }}</span>
              <v-spacer />
              <span class="mancante red--text">−{{ gruppo.mancante }}</span>
            </div>

            <template v-for="serie in gruppo.serie">
              <div
                v-if="serie.nome"
                :key="gruppo.id_prodotto + '-s-' + serie.nome"
                class="serie_scoperto"
              >
                {{ serie.nome }}
              </div>
              <div
                v-for="riga in serie.righe"
                :key="riga.chiave"
                class="riga_scoperto"
                @click="vai_a(riga)"
              >
                <v-avatar size="22" class="mr-2">
                  <v-img v-if="riga.colore.img" :src="riga.colore.img" />
                  <v-icon v-else x-small>mdi-help</v-icon>
                </v-avatar>
                <span class="nome_scoperto">
                  <template v-if="riga.colore.tipo === 'varieta'">
                    <span class="nome_varieta">{{ riga.colore.nome }}</span>
                    <span class="ml-2 font-weight-medium">
                      {{ riga.colore.nome_colore }}
                    </span>
                  </template>
                  <span v-else class="font-weight-medium">{{ riga.colore.nome }}</span>
                  <v-chip
                    v-if="riga.colore.tipo !== 'varieta' && riga.ha_specie"
                    x-small
                    class="ml-2"
                    color="amber lighten-4"
                  >
                    legacy
                  </v-chip>
                </span>
                <v-spacer />
                <span class="numeri">
                  {{ riga.prodotta }} / {{ riga.ordinata }}
                </span>
                <span class="mancante red--text">−{{ riga.mancante }}</span>
              </div>
            </template>
          </div>
        </v-card-text>
        </v-expand-transition>
      </v-card>

      <div v-if="!gruppi.length" class="py-12 text-center grey--text">
        Nessun prodotto corrisponde ai filtri.
      </div>

      <div v-for="gruppo in gruppi" :key="gruppo.diametro">
        <div class="riga intestazione_gruppo" @click="alterna(chiusi, gruppo.diametro)">
          <v-icon small class="mr-2">
            {{ chiusi.includes(gruppo.diametro) ? "mdi-chevron-right" : "mdi-chevron-down" }}
          </v-icon>
          <v-icon small class="mr-2">mdi-bucket-outline</v-icon>
          <span class="font-italic grey--text">Vaso Ø {{ gruppo.diametro }}</span>
        </div>

        <template v-if="!chiusi.includes(gruppo.diametro)">
          <div v-for="prodotto in gruppo.prodotti" :key="prodotto.id" class="mb-3">
            <div
              class="riga intestazione_prodotto"
              @contextmenu.prevent="apri_menu_prodotto($event, prodotto)"
            >
              <span>{{ nome_breve(prodotto) }}</span>
              <v-chip
                v-if="specie_di(prodotto)"
                x-small
                class="ml-3"
                color="green lighten-5"
              >
                <v-icon x-small left color="green">mdi-sprout</v-icon>
                {{ specie_di(prodotto).nome }}
              </v-chip>
              <v-chip v-else x-small class="ml-3" color="amber lighten-4">
                <v-icon x-small left color="amber darken-2">mdi-alert-outline</v-icon>
                senza specie
              </v-chip>
            </div>

            <div class="riga testata">
              <div class="cella_nome"></div>
              <div class="cella_num">Prodotta</div>
              <div class="cella_num">Ordinata</div>
              <div class="cella_num">Rimanenza</div>
              <div class="cella_barra"></div>
              <div class="cella_azione"></div>
            </div>

            <template v-for="blocco in prodotto.blocchi">
              <div
                v-if="blocco.nome"
                :key="prodotto.id + '-s-' + blocco.nome"
                class="serie"
                @contextmenu.prevent="apri_menu_serie($event, prodotto, blocco)"
              >
                {{ blocco.nome }}
                <span class="text-caption grey--text ml-2">
                  {{ voci_serie(prodotto, blocco.nome).length }}
                </span>
              </div>
              <div v-for="colore in blocco.voci" :key="colore.id">
              <div
                class="riga colore"
                :class="{
                  scoperto: rimanenza(prodotto.id, colore.id) < 0,
                  esclusa: colore.esclusa,
                }"
                @contextmenu.prevent="apri_menu_riga($event, prodotto, colore)"
              >
                <div class="cella_nome">
                  <v-avatar size="30" class="mr-3">
                    <v-img v-if="colore.img" :src="colore.img" />
                    <v-icon v-else small>mdi-help</v-icon>
                  </v-avatar>
                  <span v-if="colore.tipo === 'varieta'" class="nome_varieta mr-2">
                    {{ colore.nome }}
                  </span>
                  <span class="font-weight-medium">
                    {{ colore.nome_colore || colore.nome }}
                  </span>
                  <v-chip
                    v-if="colore.tipo !== 'varieta' && prodotto.specie"
                    x-small
                    class="ml-2"
                    color="amber lighten-4"
                  >
                    legacy
                  </v-chip>
                  <v-chip v-if="colore.esclusa" x-small class="ml-2" color="grey lighten-3">
                    esclusa
                  </v-chip>
                </div>

                <div class="cella_num">
                  <CellaQta
                    :valore="prodotta(prodotto.id, colore.id)"
                    :massimo="100000"
                    @cambia="imposta(prodotto, colore, $event)"
                  />
                </div>

                <div class="cella_num valore">
                  {{ ordinato(prodotto.id, colore.id).qta || "" }}
                </div>

                <div
                  class="cella_num valore"
                  :class="classe_rimanenza(prodotto.id, colore.id)"
                >
                  {{ etichetta_rimanenza(prodotto.id, colore.id) }}
                </div>

                <div class="cella_barra">
                  <v-progress-linear
                    v-if="prodotta(prodotto.id, colore.id) || ordinato(prodotto.id, colore.id).qta"
                    :value="percentuale(prodotto.id, colore.id)"
                    :color="colore_barra(prodotto.id, colore.id)"
                    height="6"
                    rounded
                    background-color="grey lighten-3"
                  />
                </div>

                <div class="cella_azione">
                  <v-btn
                    v-if="ordinato(prodotto.id, colore.id).clienti.length"
                    icon
                    small
                    color="grey"
                    @click="apri_clienti(prodotto, colore)"
                  >
                    <v-icon small>mdi-account-multiple</v-icon>
                  </v-btn>
                </div>
              </div>

              </div>
            </template>
          </div>
        </template>
      </div>
    </template>

    <!-- Escludere sta sul tasto destro invece di occupare un'icona su
         ogni riga. Sull'intestazione di serie agisce su tutta la serie. -->
    <v-menu
      v-model="menu_ctx"
      :position-x="menu_x"
      :position-y="menu_y"
      absolute
      offset-y
    >
      <v-list v-if="ctx" dense>
        <v-list-item disabled>
          <v-list-item-title class="text-caption grey--text">
            {{ ctx.titolo }}
          </v-list-item-title>
        </v-list-item>
        <v-divider />

        <v-list-item
          v-for="azione in ctx.azioni"
          :key="azione.chiave"
          @click="applica_esclusione(azione.varieta)"
        >
          <v-list-item-icon>
            <v-icon :color="azione.colore">{{ azione.icona }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ azione.testo }}</v-list-item-title>
            <v-list-item-subtitle class="text-caption">
              {{ azione.dettaglio }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Clienti che hanno prenotato una varietà -->
    <v-dialog v-model="dialog_clienti" width="560" scrollable>
      <v-card v-if="voce_attiva">
        <v-card-title class="text-subtitle-1 grey--text">
          <v-avatar size="32" class="mr-3">
            <v-img v-if="voce_attiva.colore.img" :src="voce_attiva.colore.img" />
            <v-icon v-else small>mdi-help</v-icon>
          </v-avatar>
          <div>
            <div class="black--text">
              <span v-if="voce_attiva.colore.tipo === 'varieta'" class="nome_varieta mr-2">
                {{ voce_attiva.colore.nome }}
              </span>
              <span class="font-weight-medium">
                {{ voce_attiva.colore.nome_colore || voce_attiva.colore.nome }}
              </span>
            </div>
            <div class="text-caption">
              Ø{{ voce_attiva.prodotto.diametro }} ·
              {{ nome_breve(voce_attiva.prodotto) }}
              <template v-if="voce_attiva.colore.nome_serie">
                · {{ voce_attiva.colore.nome_serie }}
              </template>
            </div>
          </div>
        </v-card-title>

        <v-divider />

        <v-card-subtitle class="pt-3 pb-2 d-flex">
          <span class="text-caption">
            Prodotta <b>{{ prodotta(voce_attiva.prodotto.id, voce_attiva.colore.id) }}</b>
            · Ordinata <b>{{ ordinato(voce_attiva.prodotto.id, voce_attiva.colore.id).qta }}</b>
          </span>
          <v-spacer />
          <span
            class="text-caption"
            :class="classe_rimanenza(voce_attiva.prodotto.id, voce_attiva.colore.id)"
          >
            Rimanenza
            <b>{{ rimanenza(voce_attiva.prodotto.id, voce_attiva.colore.id) }}</b>
          </span>
        </v-card-subtitle>

        <v-card-text class="pt-2">
          <div
            v-for="cliente in clienti_ordinati(voce_attiva.prodotto.id, voce_attiva.colore.id)"
            :key="cliente.id_prenotazione"
            class="riga_cliente"
            @click="apri_prenotazione(cliente)"
          >
            <span class="numero_cliente">{{ cliente.numero }}</span>
            <span class="nome_cliente">{{ cliente.nome }}</span>
            <v-chip v-if="cliente.evasa" x-small class="ml-2">evasa</v-chip>
            <v-spacer />
            <span class="qta_cliente">{{ cliente.qta }}</span>
            <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text color="grey" @click="dialog_clienti = false">Chiudi</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _ from "lodash";
import resolver from "@/service/varieta_resolver";
import CellaQta from "./CellaQta.vue";

const nome_prodotto = (prodotto) => prodotto.nome.split(" in ")[0];

/**
 * Disponibilità dei crisantemi per anno.
 *
 * Risponde a una domanda sola — di cosa sono scoperto — quindi gli scoperti
 * stanno in testa e la griglia sotto serve a impostare le quantità prodotte.
 *
 * L'ordinato arriva da una mappa costruita una volta sola nello store:
 * la versione precedente riscandiva tutte le prenotazioni dell'anno a ogni
 * cella disegnata.
 */
export default {
  components: { CellaQta },

  props: {
    anno: { type: String, required: true },
  },

  data() {
    return {
      colori_per_prodotto: {},
      caricamento: true,
      errore: false,
      ricerca: "",
      solo_scoperti: false,
      mostra_escluse: false,
      menu_ctx: false,
      menu_x: 0,
      menu_y: 0,
      ctx: null,
      chiusi: [],
      dialog_clienti: false,
      voce_attiva: null,
      // Chiuso di default: il conteggio nel titolo dice già se c'è da
      // preoccuparsi, l'elenco si apre quando si vuole intervenire.
      scoperti_aperti: false,
    };
  },

  computed: {
    prodotti_crisantemo() {
      return this.$store.getters.get_lista_prodotti_raw.filter(
        (p) => p.sotto_categoria == "Crisantemo"
      );
    },

    chiave_prodotti() {
      return this.prodotti_crisantemo.map((p) => p.id).sort().join(",");
    },

    gruppi() {
      const q = (this.ricerca || "").trim().toLowerCase();
      const per_diametro = _.groupBy(this.prodotti_crisantemo, "diametro");

      return Object.keys(per_diametro)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map((diametro) => ({
          diametro,
          prodotti: per_diametro[diametro]
            .map((prodotto) => {
              const colori = this.colori_visibili(prodotto, q);
              return { ...prodotto, colori, blocchi: this.per_serie(colori) };
            })
            .filter((prodotto) => prodotto.colori.length),
        }))
        .filter((gruppo) => gruppo.prodotti.length);
    },

    /**
     * Scoperti dallo store, arricchiti con prodotto e colore per la resa.
     * Il calcolo sta là perché serve anche al segnalino sulla scheda, che
     * deve funzionare senza che questa pagina sia mai stata aperta.
     */
    scoperti() {
      return this.$store.getters["crisantemi/scoperti"]
        .map((riga) => {
          const prodotto = this.prodotti_crisantemo.find(
            (p) => p.id == riga.id_prodotto
          );
          if (!prodotto) return null;
          const colori = this.colori_per_prodotto[prodotto.id] || [];
          const colore =
            colori.find((c) => c.id === riga.id_voce) ||
            resolver.trova_voce(prodotto, riga.id_voce);
          return {
            ...riga,
            chiave: this.chiave(prodotto.id, riga.id_voce),
            diametro: prodotto.diametro,
            nome_prodotto: this.nome_breve(prodotto),
            // Serve al tag legacy: una voce e' legacy solo se il prodotto
            // una specie ce l'ha, altrimenti i colori sono la sua unica
            // sorgente e non c'e' nulla da segnalare.
            ha_specie: !!prodotto.specie,
            colore,
          };
        })
        .filter(Boolean);
    },

    /**
     * Scoperti per prodotto e serie. I prodotti sono ordinati per gravita'
     * complessiva: una lista piatta di trenta righe non dice da dove
     * cominciare, un prodotto che manca di 300 pezzi si'.
     */
    scoperti_raggruppati() {
      const per_prodotto = new Map();

      this.scoperti.forEach((riga) => {
        let gruppo = per_prodotto.get(riga.id_prodotto);
        if (!gruppo) {
          gruppo = {
            id_prodotto: riga.id_prodotto,
            diametro: riga.diametro,
            nome_prodotto: riga.nome_prodotto,
            mancante: 0,
            righe: [],
          };
          per_prodotto.set(riga.id_prodotto, gruppo);
        }
        gruppo.mancante += riga.mancante;
        gruppo.righe.push(riga);
      });

      return [...per_prodotto.values()]
        .sort((a, b) => b.mancante - a.mancante)
        .map((gruppo) => {
          const per_serie = new Map();
          gruppo.righe
            .slice()
            .sort((a, b) => {
              const serie = (a.colore.nome_serie || "").localeCompare(
                b.colore.nome_serie || ""
              );
              return serie !== 0 ? serie : b.mancante - a.mancante;
            })
            .forEach((riga) => {
              const nome = riga.colore.nome_serie || "";
              if (!per_serie.has(nome)) per_serie.set(nome, { nome, righe: [] });
              per_serie.get(nome).righe.push(riga);
            });
          return { ...gruppo, serie: [...per_serie.values()] };
        });
    },

    tutto_chiuso() {
      return this.gruppi.length > 0 && this.chiusi.length >= this.gruppi.length;
    },
  },

  watch: {
    chiave_prodotti: "carica",
    anno: "ricarica_dispo",
  },

  mounted() {
    this.carica();
    this.ricarica_dispo();
  },

  methods: {
    async carica() {
      this.errore = false;
      this.caricamento = !Object.keys(this.colori_per_prodotto).length;
      try {
        // Sempre complete: il componente deve conoscere anche le escluse
        // per poter agire su un'intera serie. Lo switch filtra a valle.
        const mappa = await resolver.carica_voci_prodotti(
          this.prodotti_crisantemo,
          { includi_escluse: true }
        );
        const oggetto = {};
        mappa.forEach((colori, id) => {
          oggetto[id] = colori;
        });
        this.colori_per_prodotto = oggetto;
      } catch {
        this.errore = true;
      } finally {
        this.caricamento = false;
      }
    },

    ricarica_dispo() {
      this.$store.dispatch("crisantemi/fetch_dispo", this.anno).catch(() => {});
    },

    /** Produzione o ordini gia' inseriti su questa voce. */
    ha_dati(id_prodotto, id_voce) {
      return (
        !!this.prodotta(id_prodotto, id_voce) ||
        !!this.ordinato(id_prodotto, id_voce).qta
      );
    },

    colori_visibili(prodotto, ricerca) {
      let colori = this.colori_per_prodotto[prodotto.id] || [];
      if (!this.mostra_escluse) {
        // Un'esclusa con dati resta visibile: nasconderla farebbe sparire
        // una produzione o un ordine che esistono.
        colori = colori.filter(
          (c) => !c.esclusa || this.ha_dati(prodotto.id, c.id)
        );
      }
      if (ricerca.length >= 2) {
        const nel_prodotto = (prodotto.nome || "").toLowerCase().includes(ricerca);
        colori = colori.filter(
          (c) =>
            nel_prodotto ||
            (c.nome || "").toLowerCase().includes(ricerca) ||
            (c.nome_colore || "").toLowerCase().includes(ricerca)
        );
      }
      if (this.solo_scoperti) {
        colori = colori.filter((c) => this.rimanenza(prodotto.id, c.id) < 0);
      }
      return colori;
    },

    chiave(id_prodotto, id_voce) {
      return `${id_prodotto}|${id_voce}`;
    },

    prodotta(id_prodotto, id_voce) {
      return this.$store.getters["crisantemi/qta_dispo"]({
        id_prodotto,
        id_voce,
      });
    },

    ordinato(id_prodotto, id_voce) {
      return this.$store.getters["crisantemi/ordinato"]({
        id_prodotto,
        id_voce,
      });
    },

    rimanenza(id_prodotto, id_voce) {
      return (
        this.prodotta(id_prodotto, id_voce) -
        this.ordinato(id_prodotto, id_voce).qta
      );
    },

    etichetta_rimanenza(id_prodotto, id_voce) {
      if (!this.prodotta(id_prodotto, id_voce) && !this.ordinato(id_prodotto, id_voce).qta) {
        return "";
      }
      return this.rimanenza(id_prodotto, id_voce);
    },

    classe_rimanenza(id_prodotto, id_voce) {
      const valore = this.rimanenza(id_prodotto, id_voce);
      if (valore < 0) return "red--text font-weight-bold";
      if (valore === 0) return "green--text";
      return "";
    },

    percentuale(id_prodotto, id_voce) {
      const prodotta = this.prodotta(id_prodotto, id_voce);
      const ordinata = this.ordinato(id_prodotto, id_voce).qta;
      if (!prodotta) return ordinata ? 100 : 0;
      return Math.min(100, (ordinata / prodotta) * 100);
    },

    colore_barra(id_prodotto, id_voce) {
      const rimanenza = this.rimanenza(id_prodotto, id_voce);
      if (rimanenza < 0) return "red";
      if (this.percentuale(id_prodotto, id_voce) > 85) return "orange";
      return "green";
    },

    imposta(prodotto, voce, qta) {
      this.$store.dispatch("crisantemi/imposta_dispo", {
        id_prodotto: prodotto.id,
        id_voce: voce.id,
        tipo: voce.tipo,
        qta,
      });
    },

    /**
     * Varietà del prodotto, tutte: i menu di esclusione devono agire
     * sull'insieme reale, non su quello filtrato da ricerca e switch.
     * "Escludi tutta la serie" con una ricerca attiva ne prenderebbe solo
     * alcune, e nessuno se ne accorgerebbe.
     */
    voci_complete(prodotto) {
      return (this.colori_per_prodotto[prodotto.id] || []).filter(
        (v) => v.tipo === "varieta"
      );
    },

    voci_serie(prodotto, nome_serie) {
      return this.voci_complete(prodotto).filter(
        (v) => (v.nome_serie || "") === (nome_serie || "")
      );
    },

    /** Costruisce le azioni escludi/reintegra per un insieme di voci. */
    azioni_per(prodotto, voci, etichetta_tutto) {
      const escluse = prodotto.varieta_escluse || [];
      const ids = voci.map((v) => v.id);
      const attive = voci.filter((v) => !v.esclusa);
      const nascoste = voci.filter((v) => v.esclusa);
      const azioni = [];

      if (attive.length) {
        azioni.push({
          chiave: "escludi",
          testo: `Escludi ${etichetta_tutto}`,
          dettaglio: `${attive.length} varietà non prodotte in questo vaso`,
          icona: "mdi-eye-off",
          colore: "amber darken-2",
          varieta: [...new Set([...escluse, ...ids])],
        });
      }
      if (nascoste.length) {
        azioni.push({
          chiave: "reintegra",
          testo: `Reintegra ${etichetta_tutto}`,
          dettaglio: `${nascoste.length} varietà torneranno prenotabili`,
          icona: "mdi-eye",
          colore: "green",
          varieta: escluse.filter((id) => !ids.includes(id)),
        });
      }
      return azioni;
    },

    posiziona_menu(evento) {
      this.menu_x = evento.clientX;
      this.menu_y = evento.clientY;
      // Chiuso e riaperto: riposizionarlo mentre è aperto lo lascia dov'era.
      this.menu_ctx = false;
      this.$nextTick(() => {
        this.menu_ctx = true;
      });
    },

    apri_menu_riga(evento, prodotto, colore) {
      // Solo le varietà si escludono: un colore legacy è l'unica sorgente
      // che quel prodotto ha, e nasconderlo lo lascerebbe senza righe.
      if (colore.tipo !== "varieta") return;
      const escluse = prodotto.varieta_escluse || [];
      this.ctx = {
        prodotto,
        titolo: colore.nome,
        azioni: [
          colore.esclusa
            ? {
                chiave: "reintegra",
                testo: "Reintegra varietà",
                dettaglio: "Tornerà prenotabile in questo vaso",
                icona: "mdi-eye",
                colore: "green",
                varieta: escluse.filter((id) => id !== colore.id),
              }
            : {
                chiave: "escludi",
                testo: "Escludi varietà",
                dettaglio: "Non prodotta in questo vaso",
                icona: "mdi-eye-off",
                colore: "amber darken-2",
                varieta: [...escluse, colore.id],
              },
        ],
      };
      this.posiziona_menu(evento);
    },

    /**
     * Azione sull'intera serie: con trenta varietà per prodotto escluderle
     * una a una non è praticabile.
     */
    apri_menu_serie(evento, prodotto, blocco) {
      const voci = this.voci_serie(prodotto, blocco.nome);
      if (!voci.length) return;
      this.ctx = {
        prodotto,
        titolo: `Serie ${blocco.nome}`,
        azioni: this.azioni_per(prodotto, voci, "tutta la serie"),
      };
      this.posiziona_menu(evento);
    },

    /**
     * Azione su tutto il prodotto: su un vaso che ne produce tre o quattro
     * conviene escludere tutto e reintegrare le poche necessarie.
     */
    apri_menu_prodotto(evento, prodotto) {
      const voci = this.voci_complete(prodotto);
      if (!voci.length) return;
      this.ctx = {
        prodotto,
        titolo: `${nome_prodotto(prodotto)} · ${voci.length} varietà`,
        azioni: this.azioni_per(prodotto, voci, "tutte le varietà"),
      };
      this.posiziona_menu(evento);
    },

    async applica_esclusione(varieta) {
      this.menu_ctx = false;
      const id_prodotto = this.ctx.prodotto.id;
      await this.$store.dispatch("imposta_varieta_escluse", {
        id_prodotto,
        varieta,
      });
      // Le voci in cache portano il flag `esclusa` calcolato al caricamento:
      // senza rilettura la griglia resterebbe indietro di una modifica.
      await this.carica();
    },

    apri_clienti(prodotto, colore) {
      this.voce_attiva = { prodotto, colore };
      this.dialog_clienti = true;
    },

    apri_prenotazione(cliente) {
      this.dialog_clienti = false;
      this.$emit("apri", {
        id: cliente.id_prenotazione,
        colore: this.voce_attiva.colore.id,
        prodotto: this.voce_attiva.prodotto.id,
      });
    },

    clienti_ordinati(id_prodotto, id_voce) {
      return this.ordinato(id_prodotto, id_voce)
        .clienti.map((c) => {
          const cliente = this.$store.getters.get_cliente_by_iva(c.iva);
          return {
            ...c,
            numero: cliente ? cliente.numero : "—",
            nome: cliente ? cliente.nome : c.iva,
          };
        })
        .sort((a, b) => b.qta - a.qta);
    },

    /** Dagli scoperti si salta al colore nella griglia, aprendone il gruppo. */
    vai_a(riga) {
      this.chiusi = this.chiusi.filter((d) => d !== riga.diametro);
      const prodotto = this.prodotti_crisantemo.find(
        (p) => p.id == riga.id_prodotto
      );
      if (prodotto) this.apri_clienti(prodotto, riga.colore);
    },

    /**
     * Voci raggruppate per serie, nell'ordine in cui arrivano dal resolver
     * (già ordinato per serie). `colori` resta piatto per i conteggi.
     */
    per_serie(voci) {
      const blocchi = [];
      voci.forEach((voce) => {
        const nome = voce.nome_serie || "";
        const ultimo = blocchi[blocchi.length - 1];
        if (ultimo && ultimo.nome === nome) ultimo.voci.push(voce);
        else blocchi.push({ nome, voci: [voce] });
      });
      return blocchi;
    },

    /** Specie collegata al prodotto, se assegnata. */
    specie_di(prodotto) {
      if (!prodotto.specie) return null;
      return this.$store.getters.get_specie_by_id(prodotto.specie) || null;
    },

    nome_breve(prodotto) {
      return prodotto.nome.split(" in ")[0];
    },

    alterna(lista, id) {
      const i = lista.indexOf(id);
      if (i === -1) lista.push(id);
      else lista.splice(i, 1);
    },

    alterna_tutto() {
      this.chiusi = this.tutto_chiuso ? [] : this.gruppi.map((g) => g.diametro);
    },
  },
};
</script>

<style scoped>
.riga {
  display: grid;
  grid-template-columns: minmax(220px, 1.4fr) 110px 90px 100px minmax(120px, 1fr) 48px;
  align-items: center;
  column-gap: 10px;
  padding: 2px 8px;
}

.intestazione_gruppo {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.intestazione_prodotto {
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  padding: 6px 8px 6px 20px;
  font-weight: 500;
  font-size: 1.02rem;
}

.testata {
  font-size: 0.72rem;
  color: rgba(0, 0, 0, 0.5);
  padding-top: 6px;
}

.colore {
  padding-top: 3px;
  padding-bottom: 3px;
}

.colore:hover {
  background: rgba(0, 0, 0, 0.02);
}

.scoperto {
  background: rgba(244, 67, 54, 0.06);
}

/* Esclusa: visibile ma in secondo piano, perché è li' solo per lo storico
   o perché la stai per reintegrare. */
.esclusa {
  opacity: 0.55;
}

.colore,
.serie,
.intestazione_prodotto {
  cursor: context-menu;
}

.nome_varieta {
  color: rgba(0, 0, 0, 0.55);
}

.serie {
  padding: 6px 8px 2px 28px;
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: rgba(0, 0, 0, 0.55);
}

.cella_nome {
  display: flex;
  align-items: center;
  min-width: 0;
  padding-left: 20px;
  overflow: hidden;
  white-space: nowrap;
}

.cella_num,
.cella_barra,
.cella_azione {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.valore {
  padding-right: 8px;
}

/* Scoperti in testa */
.scoperti {
  border-color: rgba(244, 67, 54, 0.4);
}

.testata_scoperto {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background: rgba(244, 67, 54, 0.07);
  border-radius: 4px;
  font-weight: 500;
}

.serie_scoperto {
  padding: 4px 8px 1px 20px;
  font-size: 0.76rem;
  font-style: italic;
  color: rgba(0, 0, 0, 0.55);
}

.riga_scoperto {
  display: flex;
  align-items: center;
  padding: 3px 4px 3px 20px;
  cursor: pointer;
  border-radius: 4px;
}

.riga_scoperto:hover {
  background: rgba(244, 67, 54, 0.06);
}

.nome_scoperto {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.numeri {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  font-variant-numeric: tabular-nums;
}

.mancante {
  min-width: 64px;
  text-align: right;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* Clienti di una varietà */
.riga_cliente {
  display: flex;
  align-items: center;
  padding: 6px 4px;
  cursor: pointer;
  border-radius: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.riga_cliente:hover {
  background: rgba(33, 150, 243, 0.07);
  color: #1976d2;
}

.qta_cliente {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.numero_cliente {
  min-width: 44px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.nome_cliente {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
